/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

var ProductRating = class extends HTMLElement {
  connectedCallback() {
    const optionsFromDataset = {};
    const { ratingSnippetClass, sku } = this.dataset;
    if (!sku) {
      return
    }
    if (!ratingSnippetClass)
      console.error("Attribute data-rating-snippet-class missing for:", this);
    if (!sku) console.error("Attribute data-sku missing for:", this);
    // console.log('onlyQuantity', onlyQuantity);
    if (this.dataset.hasOwnProperty("withText")) {
      optionsFromDataset.text = "Bewertungen";
      optionsFromDataset.singularText = "Bewertung";
    }
    this.initProductReviews(ratingSnippetClass, optionsFromDataset);
  }

  initProductReviews(ratingSnippetClass, options = {}) {
    const defaults = {
      store: reviewsIoStore,
      mode: "default",
      color: "#0E1311",
      linebreak: false,
      lang: reviewsIoLang,
      usePolaris: false,
      showEmptyStars: false,
      formatReviewCount: true,
    };

    if (typeof ratingSnippet !== "function")
      console.error("ratingSnippet is not available for:", this);
    else ratingSnippet(ratingSnippetClass, { ...defaults, ...options });
  }
};

customElements.define("product-rating", ProductRating);

var RatingStars = class extends HTMLElement {
  constructor() {
    super();
    const { reviewsRating, reviewsCount } = this.dataset;
    this.rating = Number(reviewsRating);
    this.count = reviewsCount ? Number(reviewsCount) : null;
  }

  connectedCallback() {
    this.render();

    const observer = new MutationObserver(() => {
      const hasContent = this.querySelector(".ruk-rating-snippet-count");
      if (!hasContent) this.render();
    });

    observer.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  render() {
    this.innerHTML = `
      ${this.renderStars()}
      ${this.renderReviews()}
    `;
  }

  renderStars() {
    return Array.from(Array(5).keys())
      .map((value) => {
        const starPercentage = (() => {
          const positionValue = this.rating - value;
          if (positionValue >= 1) {
            return 100;
          } else if (positionValue >= 0.75) {
            return 75;
          } else if (positionValue >= 0.5) {
            return 50;
          } else if (positionValue >= 0.25) {
            return 25;
          } else {
            return 0;
          }
        })();
        return `<i class="ruk-icon-percentage-star--${starPercentage}"></i>`;
      })
      .join("");
  }

  renderReviews() {
    if (this.count === null) return "";

    return `
      <span class="ruk-rating-snippet-count">(${this.count})</span>
    `;
  }
};

customElements.define("rating-stars", RatingStars);

var DoofinderFlags = class extends HTMLElement {
  connectedCallback() {
    this.renderCustomFlags();

    const observer = new MutationObserver(() => {
      this.renderCustomFlags();
    });

    observer.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  renderCustomFlags() {
    if (this.dataset.customFlagsLoaded === "true") return;
    const tags = this.dataset.tags.split("/");
    const customFlags = [];

    tags.forEach((tag) => {
      if (tag.includes("__label")) {
        const translated = window.labelTranslations && window.labelTranslations[tag];
        const label = translated || tag.split(":")[1];
        const labelIndex = tag.split(":")[0].replace("__label", "");
        customFlags.push(
          `<div class="dfd-card-flag dfd-card-flag--custom${labelIndex}">${label}</div>`
        );
      }
    });

    if (customFlags.length > 0) {
      this.insertAdjacentHTML("beforeEnd", customFlags.join(""));
      this.dataset.customFlagsLoaded = true;
    }
  }
};

customElements.define("doofinder-flags", DoofinderFlags);

var YoutubeEmbed = class extends HTMLElement {
  connectedCallback() {
    // Zuerst nur einen Platzhalter mit korrektem Seitenverhaeltnis rendern. Das
    // iframe folgt erst, wenn das Element sichtbar ist (Video-Tab geoeffnet):
    // YouTube waehlt sein Poster anhand der iframe-Groesse beim Laden, und in
    // einem noch per [hidden] ausgeblendeten Tab waere die 0 -> unscharfes
    // 120x90-Vorschaubild. Nebeneffekt: der Player wird nur geladen, wenn der
    // Tab wirklich geoeffnet wird.
    this.innerHTML = `
      <div style="max-width: 1000px; width: 100%;">
        <div class="youtube-embed__frame" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; background: rgba(0, 0, 0, 0.05);"></div>
      </div>
    `;

    if (!("IntersectionObserver" in window)) {
      this.loadIframe();
      return;
    }

    this._observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this._observer.disconnect();
        this.loadIframe();
      },
      { rootMargin: "200px" }
    );

    this._observer.observe(this);
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
  }

  loadIframe() {
    if (this._loaded) return;
    this._loaded = true;

    const embedUrl = this.generateEmbedUrl(this.getVideoId());

    this.querySelector(".youtube-embed__frame").innerHTML = `
      <iframe title="Video" src="${embedUrl}" allow="autoplay; fullscreen"
        style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0;"></iframe>
    `;

    // Videos sind auf Deutsch. Ist eine andere Sprache aktiv (von Liquid via
    // data-cc-lang gesetzt), die automatisch uebersetzten Untertitel erzwingen.
    if (this.dataset.ccLang) {
      this.enableTranslatedCaptions(this.dataset.ccLang);
    }
  }

  generateEmbedUrl(videoId) {
    const searchParams = new URLSearchParams();
    searchParams.append("rel", "0");
    searchParams.append("playsinline", "1");

    // Sprache kommt aus Liquid (request.locale.iso_code); nur bei Nicht-DE
    // gesetzt -> Untertitel in der aktiven Sprache einblenden.
    const ccLang = this.dataset.ccLang;
    if (ccLang) {
      searchParams.append("enablejsapi", "1");
      searchParams.append("cc_load_policy", "1");
      searchParams.append("cc_lang_pref", ccLang);
      searchParams.append("hl", ccLang);
    }

    return `https://www.youtube.com/embed/${videoId}?${searchParams.toString()}`;
  }

  // cc_lang_pref waehlt nur eine vorhandene Spur. Fuer die automatische
  // Uebersetzung (DE -> z.B. FR) per postMessage die deutsche Basisspur in die
  // Zielsprache uebersetzen lassen (gleicher Ansatz wie <external-video>).
  enableTranslatedCaptions(lang) {
    const iframe = this.querySelector("iframe");
    const command = (func, args) =>
      iframe.contentWindow.postMessage(JSON.stringify({ event: "command", func, args }), "*");

    iframe.addEventListener("load", () => {
      setTimeout(() => command("setOption", ["captions", "reload", true]), 600);
      setTimeout(() => command("setOption", ["captions", "track", { languageCode: "de", translationLanguage: { languageCode: lang } }]), 1200);
    });
  }

  getVideoId() {
    const url = new URL(this.dataset.videoUrl);
    const searchParams = new URLSearchParams(url.search);
    return searchParams.get("v");
  }
};

customElements.define("youtube-embed", YoutubeEmbed);

var ProductReviews = class extends HTMLElement {
  connectedCallback() {
    this.config = this.getConfig();
    this.els = this.getEls();

    this._currentPage = 1;

    this.addEventListeners();
  }

  addEventListeners() {
    this.els.paginationNavItems.forEach((nav) => {
      nav.addEventListener("click", (event) => {
        this.currentPage = Number(event.currentTarget.dataset.pageIndex);
      });
    });
  }

  showReview(index) {
    this.els.reviewItems
      .find((reviewItem) => Number(reviewItem.dataset.index) === index)
      .removeAttribute("hidden");
  }

  hideAllReviews() {
    this.els.reviewItems.forEach((reviewItem) =>
      reviewItem.setAttribute("hidden", true)
    );
  }

  updateNavigation() {
    this.els.paginationNavItems.forEach((navItem) =>
      navItem.classList.remove("product-reviews__pagination-nav--current")
    );
    this.els.paginationNavItems
      .find((navItem) => Number(navItem.dataset.pageIndex) === this.currentPage)
      .classList.add("product-reviews__pagination-nav--current");
  }

  scrollTop() {
    this.scrollIntoView({ block: "start", behavior: "smooth" });
  }

  set currentPage(value) {
    this._currentPage = value;
    this.hideAllReviews();
    this.updateNavigation();

    this.els.reviewItems.forEach((item) => {
      const itemIndex = Number(item.dataset.index);

      if (
        itemIndex >= this.config.resultsPerPage * (this.currentPage - 1) &&
        itemIndex < this.config.resultsPerPage * this.currentPage
      ) {
        item.removeAttribute("hidden");
      }
    });

    this.scrollTop();
  }

  get currentPage() {
    return this._currentPage;
  }

  getConfig() {
    return {
      resultsPerPage: Number(this.dataset.resultsPerPage),
    };
  }

  getEls() {
    return {
      reviewItems: [...this.querySelectorAll(".product-reviews__item")],
      paginationNavItems: [
        ...this.querySelectorAll(".product-reviews__pagination-nav"),
      ],
    };
  }
};

customElements.define("product-reviews", ProductReviews);

var LazyLoadContainer = class extends HTMLDivElement {
  connectedCallback() {
    this.loaded = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadImage();
        }
      });
    });

    observer.observe(this);
  }

  loadImage() {
    this.loaded = true;
    const imageEl = this.querySelector('[lazyload-image]');

    if (imageEl && !imageEl.getAttribute('lazyloaded')) {
      imageEl.src = imageEl.dataset.src;
      imageEl.setAttribute('lazyloaded', true);
    }
  }
};

customElements.define("lazyload-container", LazyLoadContainer, {
  extends: "div",
});



/**
// START Section Rendering for combined Listing
**/


function buildSectionRenderingUrl(productUrl, sectionIds, optionValues) {
  const url = new URL(productUrl, window.location.origin);
  url.searchParams.set("sections", sectionIds.join(","));
  if (optionValues && optionValues.length > 0) {
    url.searchParams.set("option_values", optionValues.join(","));
  }
  return url.toString();
}

function getSelectedOptionValueIds(button) {
  const variantPicker = button.closest('variant-picker');
  if (!variantPicker) return [];
  const ids = new Set();
  variantPicker.querySelectorAll('.block-swatch__item.is-selected[data-option-value-id]').forEach(el => ids.add(el.dataset.optionValueId));
  variantPicker.querySelectorAll('.block-swatch__radio:checked, .color-swatch__radio:checked, .variant-swatch__radio:checked').forEach(radio => ids.add(radio.value));
  return [...ids];
}

function allOptionGroupsSelected(productForm) {
  if (!productForm) return false;
  var groups = productForm.querySelectorAll('.product-form__option-selector');
  if (groups.length === 0) return true;
  for (var i = 0; i < groups.length; i++) {
    if (!groups[i].querySelector('.is-selected, :checked, [aria-selected="true"]')) {
      return false;
    }
  }
  return true;
}

function applyBisVisibility() {
  var btn = document.getElementById('AddToCart');
  if (!btn) return;
  var container = btn.closest('product-payment-container');
  var bis = container && container.querySelector('#hb-bis-btn');
  if (bis && !document.getElementById('hb-bis-overlay')) bis = null;
  if (!bis) return;
  var unavailable = container.querySelector('.product-form__unavailable-button');

  // Radios liegen im variant-picker, außerhalb des <form> – daher auf .product-form suchen
  var productForm = btn.closest('.product-form');
  var fakeVariantSelected = productForm && !!productForm.querySelector('.block-swatch__radio[data-real-variant="false"]:checked');
  var quantityBlock = productForm && productForm.querySelector('[data-block-type="quantity-selector"]');
  if (fakeVariantSelected) {
    btn.style.display = 'none';
    bis.style.display = 'none';
    if (unavailable) unavailable.style.display = allOptionGroupsSelected(productForm) ? '' : 'none';
    if (allOptionGroupsSelected(productForm) && quantityBlock) quantityBlock.remove();
  } else if (btn.disabled) {
    var bisSelect = document.getElementById('hb-bis-variant-select');
    var hasUnavailableVariants = bisSelect && bisSelect.options.length > 0;
    btn.style.display = 'none';
    bis.style.display = hasUnavailableVariants ? 'block' : 'none';
    if (unavailable) unavailable.style.display = 'none';
  } else {
    btn.style.display = '';
    bis.style.display = 'none';
    if (unavailable) unavailable.style.display = 'none';
  }

  var allSelected = allOptionGroupsSelected(productForm);
  var chooseText = (window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.productFormChooseVariant) || 'Bitte Variante auswählen';

  // "Konfiguration nicht erhältlich" unsichtbar machen (Platz reservieren), wenn nicht alle Option-Groups eine Auswahl haben
  var inventoryUnavailable = productForm && productForm.querySelector('.inventory--unavailable');
  if (inventoryUnavailable) {
    inventoryUnavailable.style.visibility = allSelected ? '' : 'hidden';
  }

  // Wert der letzten Option durch "Bitte Variante auswählen" ersetzen, wenn keine echte Variante ausgewählt ist
  if (productForm) {
    var optionSelectors = productForm.querySelectorAll('.product-form__option-selector');
    if (optionSelectors.length > 0) {
      var lastSelector = optionSelectors[optionSelectors.length - 1];
      var valueSpan = lastSelector.querySelector('.product-form__option-value');
      if (valueSpan) {
        if (valueSpan.dataset.originalValue === undefined) {
          valueSpan.dataset.originalValue = valueSpan.textContent;
        }
        if (!allSelected) {
          valueSpan.textContent = chooseText;
          valueSpan.style.color = '#ffb71b';
        } else {
          valueSpan.textContent = valueSpan.dataset.originalValue;
          valueSpan.style.color = '';
        }
      }
    }
  }
}

function initBisVisibility() {
  applyBisVisibility();

  var btn = document.getElementById('AddToCart');
  var form = btn && btn.closest('form');
  if (form && !form._bisListenerAdded) {
    form._bisListenerAdded = true;
    form.addEventListener('product:rerender', applyBisVisibility);

    // Capture phase: Fragment-Zustand lesen BEVOR theme.js Nodes verschiebt
    form.addEventListener('product:rerender', function(e) {
      var htmlFragment = e.detail && e.detail.htmlFragment;
      if (!htmlFragment) return;

      var hasQty = !!htmlFragment.querySelector('[data-block-type="quantity-selector"]');
      var qtyClone = hasQty
        ? htmlFragment.querySelector('[data-block-type="quantity-selector"]').cloneNode(true)
        : null;

      // Nach theme.js DOM-Korrektur anwenden
      setTimeout(function() {
        var productForm = document.getElementById('MainProductFormBuyButtons');
        if (!productForm) return;
        var currentQty = productForm.querySelector('[data-block-type="quantity-selector"]');

        if (!hasQty && currentQty) {
          currentQty.remove();
        } else if (hasQty && !currentQty) {
          var buyButtons = productForm.querySelector('[data-block-type="buy-buttons"]');
          if (buyButtons) buyButtons.before(qtyClone);
        }
      }, 0);
    }, { capture: true });
  }
}

// Einmaliger document-level Listener für Radio-Änderungen (Radios sind außerhalb des <form>)
if (!document._blockSwatchRadioListenerAdded) {
  document._blockSwatchRadioListenerAdded = true;
  document.addEventListener('change', function(e) {
    if (e.target.classList.contains('block-swatch__radio')) {
      applyBisVisibility();
    }
  });
}




function getAllSectionIds() {

  const sectionIdentifier = {
    "productMainSection": ".shopify-section--main-product",
    "descriptionSection": ".shopify-section--product-content",
    "gridSection": ".shopify-section--grid-view",
    // "recommendationSection": ".shopify-section--product-recommendations-tabs"
  }

  const ids = [];
  Object.values(sectionIdentifier).forEach(selector => {
    const section = document.querySelector(selector);
    if (!section?.id) return;
    ids.push(section.id.replace("shopify-section-", ""));
  });

  return ids;
}

document.addEventListener("click", async (event) => {
  const button = event.target.closest("[data-block-swatch-item]");
  if (!button) return;

  const parentSwatch = button.closest(".block-swatch");
  if (parentSwatch?.classList.contains("is-disabled")) return;

  const productUrl = button.dataset.productUrl;

  const sectionIds = getAllSectionIds();
  if(!sectionIds || !productUrl) return null;

  event.preventDefault();

  const swatchList = button.closest('.block-swatch-list');
  if (swatchList) {
    swatchList.querySelectorAll('.block-swatch__item').forEach(item => item.classList.remove('is-selected'));
    button.classList.add('is-selected');
  }

  const optionValues = getSelectedOptionValueIds(button);

  button.disabled = true;
  button.setAttribute("aria-busy", "true");

  try {
    const response = await fetch(buildSectionRenderingUrl(productUrl, sectionIds, optionValues), {
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (!response.ok) {
      throw new Error(`Section rendering request failed: ${response.status}`);
    }

    const sections = await response.json();

    await sectionIds.forEach((sectionId) => {
      const sectionHtml = sections?.[sectionId];
      document.querySelector("#shopify-section-" + sectionId).innerHTML = sectionHtml;
    })

    initBisVisibility();
    fitProductTitle();
    setupFitTitleObserver();

    window.history.replaceState({}, '', productUrl);

  } catch (error) {
    if (error.name === "AbortError") return;

    console.error("Block swatch section rendering failed:", error);
    window.location.href = productUrl;
  } finally {

    button.disabled = false;
    button.removeAttribute("aria-busy");
  }
});






function fitProductTitle() {
  var title = document.querySelector('.shopify-section--main-product .product-meta__title');
  if (!title) return;
  title.style.fontSize = '';
  title.style.whiteSpace = 'nowrap';
  var size = parseFloat(window.getComputedStyle(title).fontSize);
  var min = 20;
  while (title.scrollWidth > title.offsetWidth && size > min) {
    size -= 0.5;
    title.style.fontSize = size + 'px';
  }
  if (title.scrollWidth > title.offsetWidth) {
    title.style.whiteSpace = 'normal';
  }
}

function setupFitTitleObserver() {
  var form = document.querySelector('.shopify-section--main-product form[id^="product-form-main-"]');
  if (!form || form._fitTitleListenerAdded) return;
  form._fitTitleListenerAdded = true;
  form.addEventListener('product:rerender', fitProductTitle);
}

window.addEventListener('resize', fitProductTitle);

document.addEventListener('DOMContentLoaded', function () {
  fitProductTitle();
  setupFitTitleObserver();
  initBisVisibility();

  const buttons = document.querySelectorAll('[data-block-swatch-item]')
  const sectionIds = getAllSectionIds();


  const urls = [];

  buttons.forEach(button => {
    const productUrl = button.dataset.productUrl;

    if (!productUrl) return;

    // optional: sicherstellen, dass section_id angehängt wird
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('sections', sectionIds.join(","));
    urls.push(url.pathname + url.search);
  });


  if (urls.length === 0) return;

  if (HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) {

    const specScript = document.createElement('script');
    specScript.type = 'speculationrules';
    specScript.textContent = JSON.stringify({
      prefetch: [
        {
          source: 'list',
          urls: urls,
          eagerness: 'immediate'
        }
      ]
    });

    document.head.append(specScript);
  } else {
  // 2. Wahl: Klassisches Prefetching (Fallback für Safari/Firefox/etc.)
  urls.forEach((url) => {
    const prefetchLink = document.createElement('link');
    prefetchLink.rel = 'prefetch';
    prefetchLink.href = url;
    document.head.append(prefetchLink);
  })
  console.log('Mode: Prefetching (Fallback)');
  }


});


/**
 * START Quick-View Rerendering for combined Listing
 */

document.addEventListener("click", async (event) => {
  const button = event.target.closest("[quick-data-block-swatch-item]");
  if (!button) return;

  const parentSwatch = button.closest(".block-swatch");
  if (parentSwatch?.classList.contains("is-disabled")) return;

  const productUrl = button.dataset.productUrl;

  if(!productUrl) return null;

  event.preventDefault();

  const swatchList = button.closest('.block-swatch-list');
  if (swatchList) {
    swatchList.querySelectorAll('.block-swatch__item').forEach(item => item.classList.remove('is-selected'));
    button.classList.add('is-selected');
  }

  button.disabled = true;
  button.setAttribute("aria-busy", "true");

  let quickBuy = button.closest("quick-buy-drawer");

  if(!quickBuy || !quickBuy.id) {
    quickBuy = button.closest("quick-buy-popover");
  }

  if(!quickBuy || !quickBuy.id) return null;

  const drawer = document.querySelector('#' + quickBuy.id);

  const scrollContainer = drawer.querySelector('.popover__content') || drawer.querySelector('.drawer__content');
  const savedScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

  if (savedScrollTop > 0) {
    drawer.addEventListener('openable-element:load:end', () => {
      requestAnimationFrame(() => {
        const newContainer = drawer.querySelector('.popover__content') || drawer.querySelector('.drawer__content');
        if (newContainer) {
          newContainer.scrollTop = savedScrollTop;
        }
      });
    }, { once: true });
  }

  // drawer.setAttribute('href', productUrl);
  drawer.setAttribute('href', productUrl + "?view=quick-buy-drawer");
  drawer._isLoading = false;
  drawer.open = true;
});

/**
 * Scroll-Position im Quick-Buy-Popover bei Variantenwechsel beibehalten
 */
document.addEventListener('variant:changed', (event) => {
  const popover = event.target.closest('quick-buy-popover');
  if (!popover) return;

  const scrollContainer = popover.querySelector('.popover__content');
  if (!scrollContainer) return;

  const savedScrollTop = scrollContainer.scrollTop;

  const observer = new MutationObserver(() => {
    observer.disconnect();
    requestAnimationFrame(() => {
      const newContainer = popover.querySelector('.popover__content');
      if (newContainer) {
        newContainer.scrollTop = savedScrollTop;
      }
    });
  });

  observer.observe(popover, { childList: true, subtree: true });
}, true);

/**
 * START Speculationrules
 */

if (HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) {
  // 1. Wahl: Volles Prerendering für Chromium-Browser
  const specScript = document.createElement('script');
  specScript.type = 'speculationrules';
  specScript.textContent = JSON.stringify({
    prerender: [{
      where: {
        or: [
            {
                "href_matches": "/(products|collections|pages|search|shop|blogs|policies){/*}?"
            },
            {
                "href_matches": "/([a-z]{2,3}|zh-hans|zh-hant)(-[a-z]{2,3})?/(products|collections|pages|search|shop|blogs|policies){/*}?"
            },
            {
                "href_matches": "/"
            },
            {
                "href_matches": "/([a-z]{2,3}|zh-hans|zh-hant)(-[a-z]{2,3})?{/}?"
            }
        ]
      },
      eagerness: "moderate"
    }]
  });
  document.head.append(specScript);
  console.log('Mode: Prerendering (Speculation Rules)');

}
// else {
//   // 2. Wahl: Klassisches Prefetching (Fallback für Safari/Firefox/etc.)
//   const prefetchLink = document.createElement('link');
//   prefetchLink.rel = 'prefetch';
//   prefetchLink.href = targetUrl;
//   document.head.append(prefetchLink);
//   console.log('Mode: Prefetching (Fallback)');
// }


/**
 * Custom Back-In-Stock Modal
 */
(function () {
  function openBisModal() {
    var overlay = document.getElementById('hb-bis-overlay');
    if (!overlay) return;

    // Aktuell gewählte Variante aus dem Formular lesen
    var variantInput = document.querySelector('form input[name="id"]:not([disabled])');
    if (variantInput) {
      var select = document.getElementById('hb-bis-variant-select');
      if (select) select.value = variantInput.value;
    }

    // Formular zurücksetzen
    var emailInput = document.getElementById('hb-bis-email');
    if (emailInput) emailInput.value = '';
    var errorEl = document.getElementById('hb-bis-error');
    if (errorEl) errorEl.hidden = true;
    var successEl = document.getElementById('hb-bis-success');
    if (successEl) successEl.hidden = true;
    var variantField = document.getElementById('hb-bis-variant-field');
    if (variantField) variantField.hidden = false;
    if (emailInput) emailInput.hidden = false;
    var submitBtn = overlay.querySelector('.hb-bis-submit');
    if (submitBtn) { submitBtn.hidden = false; submitBtn.disabled = false; }
    var newsletterDiv = overlay.querySelector('#hb-bis-newsletter')?.closest('.hb-bis-field');
    if (newsletterDiv) newsletterDiv.hidden = false;

    overlay.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  function closeBisModal() {
    var overlay = document.getElementById('hb-bis-overlay');
    if (!overlay) return;
    overlay.hidden = true;
    document.body.style.overflow = '';
  }

  // Button-Klick
  document.addEventListener('click', function (e) {
    if (e.target.closest('#hb-bis-btn')) {
      e.preventDefault();
      openBisModal();
    }
  });

  // Schließen-Button & Success-Link
  document.addEventListener('click', function (e) {
    if (e.target.closest('#hb-bis-close-btn') || e.target.closest('#hb-bis-success-close')) {
      e.preventDefault();
      closeBisModal();
    }
  });

  // Klick außerhalb
  document.addEventListener('click', function (e) {
    if (e.target.id === 'hb-bis-overlay') closeBisModal();
  });

  // Escape-Taste
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeBisModal();
  });

  // Formular absenden
  document.addEventListener('submit', function (e) {
    if (e.target.id !== 'hb-bis-form') return;
    e.preventDefault();

    var config = window._hbBISConfig;
    if (!config) return;

    var variantId = document.getElementById('hb-bis-variant-select')?.value;
    var email = document.getElementById('hb-bis-email')?.value;
    var newsletterChecked = document.getElementById('hb-bis-newsletter')?.checked;
    var errorEl = document.getElementById('hb-bis-error');
    var successEl = document.getElementById('hb-bis-success');
    var submitBtn = e.target.querySelector('.hb-bis-submit');

    if (!email || !variantId) return;

    submitBtn.disabled = true;
    errorEl.hidden = true;

    var klaviyoHeaders = {
      'accept': 'application/json',
      'content-type': 'application/json',
      'origin': window.location.origin,
      'revision': '2024-02-15',
      'x-klaviyo-onsite': '1'
    };

    var bisBody = JSON.stringify({
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          },
          channels: ['EMAIL']
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: '$shopify:::$default:::' + variantId
            }
          }
        }
      }
    });

    var requests = [
      fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + config.apiKey, {
        method: 'POST',
        headers: klaviyoHeaders,
        body: bisBody
      })
    ];

    if (newsletterChecked && config.listId) {
      var newsletterBody = JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: { '$source': 'Back in Stock' }
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: config.listId
              }
            }
          }
        }
      });

      requests.push(
        fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + config.apiKey, {
          method: 'POST',
          headers: klaviyoHeaders,
          body: newsletterBody
        })
      );
    }

    Promise.all(requests)
      .then(function (responses) {
        var failed = responses.find(function (r) { return !r.ok; });
        if (!failed) {
          var variantField = document.getElementById('hb-bis-variant-field');
          var emailInput = document.getElementById('hb-bis-email');
          var newsletterDiv = document.getElementById('hb-bis-newsletter')?.closest('.hb-bis-field');
          if (variantField) variantField.hidden = true;
          if (emailInput) emailInput.hidden = true;
          if (newsletterDiv) newsletterDiv.hidden = true;
          if (submitBtn) submitBtn.hidden = true;
          successEl.hidden = false;
        } else {
          return failed.json().then(function (data) {
            errorEl.textContent = (data && data.errors?.[0]?.detail) || 'Ein Fehler ist aufgetreten.';
            errorEl.hidden = false;
            submitBtn.disabled = false;
          });
        }
      })
      .catch(function () {
        errorEl.textContent = 'Ein Fehler ist aufgetreten. Bitte versuche es erneut. Mögliche Ursache: AdBlocker aktiv';
        errorEl.hidden = false;
        submitBtn.disabled = false;
      });
  });
})();
